/**
 * showdownMoves.batch6 — batch 6 of the per-move Showdown animation ports.
 *
 * Each entry is a near-verbatim port of a move's script from Pokémon Showdown's
 * `BattleMoveAnims` (`battle-animations-moves.ts`, MIT/CC0). Our {@link FxScene} +
 * {@link Actor} mirror Showdown's `scene` + `Sprite`, so the literal
 * `scene.showEffect(...)` / `actor.anim(...)` / `scene.wait(...)` sequences port
 * unchanged. Where a move pointed at a shared `BattleOtherAnims` template we reuse
 * the ported template from {@link module:showdownOther}; for the handful of
 * templates not exported there (`selfstatus`, `shake`, `dance`, `spinattack`,
 * `chargestatus`, `sound`) we port them locally below (this file owns only itself).
 *
 * Effect-name discipline: only effect keys vendored in our fx catalog are used.
 * Where Showdown referenced an effect/background we did not vendor (a remote-url
 * weather background, the attacker's own sprite drawn as a streak via `attacker.sp`,
 * a CSS gradient wash), the call is adapted to the nearest vendored effect, a flat
 * `backgroundEffect` colour wash, or omitted — noted inline at each site. The motion
 * choreography is always preserved verbatim.
 *
 * Test/runtime separation: imports ONLY TYPES from `showdownFx.ts` (which pulls in
 * Phaser). Every script is a pure function over the `FxScene`/`Actor` interfaces.
 */
import type { FxScene, Actor, MoveAnim, MoveAnimEntry } from './showdownFx.js';
import { contactattack, punchattack, bite as biteTemplate } from './showdownOther.js';

/* ──────────────────────────────────────────────────────────────────────────
 * Local template ports (PS `BattleOtherAnims` entries not exported by
 * showdownOther.ts). Ported verbatim from `battle-animations.ts`.
 * ──────────────────────────────────────────────────────────────────────── */

/** PS `BattleOtherAnims.selfstatus` — two wisps collapse inward on the user. */
const selfstatus: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 2, opacity: 0.2, time: 0 },
    { scale: 0, opacity: 1, time: 300 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 2, opacity: 0.2, time: 200 },
    { scale: 0, opacity: 1, time: 500 },
    'linear',
  );
};

/** PS `BattleOtherAnims.shake` — a quick left-right shudder of the user. */
const shake: MoveAnim = (_scene: FxScene, [attacker]: Actor[]) => {
  attacker.anim({ x: attacker.x - 10, time: 200 });
  attacker.anim({ x: attacker.x + 10, time: 300 });
  attacker.anim({ x: attacker.x, time: 200 });
};

/** PS `BattleOtherAnims.dance` — a small left-right sway of the user. */
const dance: MoveAnim = (_scene: FxScene, [attacker]: Actor[]) => {
  attacker.anim({ x: attacker.x - 10 });
  attacker.anim({ x: attacker.x + 10 });
  attacker.anim({ x: attacker.x });
};

/** PS `BattleOtherAnims.spinattack` — a spinning ballistic lunge into the target. */
const spinattack: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  attacker.anim(
    { x: defender.x, y: defender.y + 60, z: defender.behind(-30), time: 400 },
    'ballistic2',
  );
  attacker.anim({ x: defender.x, y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim({ time: 500 }, 'ballistic2');
  defender.delay(450);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
  scene.wait(500);
};

/** PS `BattleOtherAnims.chargestatus` — electro orbs spiral inward to charge the user. */
const chargestatus: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'electroball',
    { x: attacker.x - 60, y: attacker.y + 40, z: attacker.z, scale: 0.7, opacity: 0.7, time: 0 },
    { x: attacker.x, y: attacker.y, scale: 0.2, opacity: 0.2, time: 300 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'electroball',
    { x: attacker.x + 60, y: attacker.y - 5, z: attacker.z, scale: 0.7, opacity: 0.7, time: 100 },
    { x: attacker.x, y: attacker.y, scale: 0.2, opacity: 0.2, time: 300 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'electroball',
    { x: attacker.x - 30, y: attacker.y + 60, z: attacker.z, scale: 0.7, opacity: 0.7, time: 100 },
    { x: attacker.x, y: attacker.y, scale: 0.2, opacity: 0.2, time: 400 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'electroball',
    { x: attacker.x + 20, y: attacker.y - 50, z: attacker.z, scale: 0.7, opacity: 0.7, time: 100 },
    { x: attacker.x, y: attacker.y, scale: 0.2, opacity: 0.2, time: 400 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'electroball',
    { x: attacker.x - 70, y: attacker.y - 50, z: attacker.z, scale: 0.7, opacity: 0.7, time: 200 },
    { x: attacker.x, y: attacker.y, scale: 0.2, opacity: 0.2, time: 500 },
    'linear',
    'fade',
  );
};

/** PS `BattleOtherAnims.sound` — three expanding wisp rings ripple off the user. */
const sound: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.7, time: 0 },
    { z: attacker.behind(-50), scale: 5, opacity: 0, time: 400 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.7, time: 150 },
    { z: attacker.behind(-50), scale: 5, opacity: 0, time: 600 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.7, time: 300 },
    { z: attacker.behind(-50), scale: 5, opacity: 0, time: 800 },
    'linear',
  );
};

/* ──────────────────────────────────────────────────────────────────────────
 * Move scripts (batch 6).
 * ──────────────────────────────────────────────────────────────────────── */

/** PS `poisonpowder` → `spore` anim (poison spores drift down onto the defender). */
const poisonpowder: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  scene.showEffect(
    'poisonwisp',
    { x: defender.x + 10, y: defender.y + 90, z: defender.z, opacity: 0, scale: 0.4 },
    { y: defender.y - 5, opacity: 1, time: 500 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'poisonwisp',
    { x: defender.x + 30, y: defender.y + 90, z: defender.z, opacity: 0, scale: 0.4, time: 150 },
    { y: defender.y - 5, opacity: 1, time: 650 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'poisonwisp',
    { x: defender.x - 30, y: defender.y + 90, z: defender.z, opacity: 0, scale: 0.4, time: 300 },
    { y: defender.y - 5, opacity: 1, time: 800 },
    'decel',
    'fade',
  );
};

/** PS `poisonsting`/`poisontail` → `poisonjab` anim (poison wisps + a jab). */
const poisonjab: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'poisonwisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 400 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(50),
      scale: 3,
      opacity: 0,
      time: 700,
    },
    'linear',
  );
  scene.showEffect(
    'poisonwisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 500 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(50),
      scale: 3,
      opacity: 0,
      time: 800,
    },
    'linear',
  );
  scene.showEffect(
    'poisonwisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 400 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(50),
      scale: 2,
      opacity: 0,
      time: 800,
    },
    'linear',
  );
  attacker.anim(
    { x: defender.leftof(20), y: defender.y, z: defender.behind(-20), time: 400 },
    'ballistic2Under',
  );
  attacker.anim({ x: defender.x, y: defender.y, z: defender.z, time: 50 });
  attacker.anim({ time: 500 }, 'ballistic2');
  defender.delay(425);
  defender.anim(
    { x: defender.leftof(-20), y: defender.y, z: defender.behind(20), time: 50 },
    'swing',
  );
  defender.anim({ time: 300 }, 'swing');
};

/** PS `pollenpuff` → `revelationdance` anim (electro charge then a wisp burst). */
const revelationdance: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.backgroundEffect('#000000', 1300, 0.3);
  scene.showEffect(
    'electroball',
    {
      x: attacker.x + 20,
      y: attacker.y - 60,
      z: attacker.behind(15),
      scale: 1,
      xscale: 2,
      opacity: 0.5,
      time: 150,
    },
    { scale: 1.5, xscale: 4, opacity: 0 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'electroball',
    {
      x: attacker.x - 20,
      y: attacker.y - 60,
      z: attacker.behind(15),
      scale: 1,
      xscale: 2,
      opacity: 0.5,
      time: 500,
    },
    { scale: 1.5, xscale: 4, opacity: 0 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'electroball',
    {
      x: attacker.x,
      y: attacker.y - 60,
      z: attacker.behind(15),
      scale: 1,
      xscale: 2,
      opacity: 0.5,
      time: 650,
    },
    { scale: 1.5, xscale: 4, opacity: 0 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'electroball',
    {
      x: attacker.x,
      y: attacker.y,
      z: attacker.behind(-15),
      scale: 0.7,
      xscale: 1,
      opacity: 0.8,
      time: 900,
    },
    { x: defender.x, y: defender.y, z: defender.behind(5), time: 1100 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x + 30, y: defender.y + 25, z: defender.z, scale: 0.2, opacity: 0.6, time: 1100 },
    { x: defender.x + 50, y: defender.y + 10, opacity: 0.3, time: 1400 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x - 30, y: defender.y - 20, z: defender.z, scale: 0.2, opacity: 0.6, time: 1200 },
    { x: defender.x - 50, y: defender.y - 20, opacity: 0.3, time: 1400 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x + 15, y: defender.y + 10, z: defender.z, scale: 0.2, opacity: 0.7, time: 1300 },
    { x: defender.x + 35, y: defender.y + 30, opacity: 0.3, time: 1500 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x - 15, y: defender.y - 30, z: defender.z, scale: 0.2, opacity: 0.7, time: 1300 },
    { x: defender.x - 35, y: defender.y - 40, opacity: 0.3, time: 1500 },
    'accel',
    'explode',
  );
  attacker.anim({ x: attacker.x + 5, y: attacker.y + 10, time: 100 }, 'ballistic');
  attacker.anim({ x: attacker.x + 10, y: attacker.y, time: 50 });
  attacker.anim({ x: attacker.x - 5, y: attacker.y + 10, time: 200 }, 'ballistic');
  attacker.anim({ x: attacker.x - 10, y: attacker.y, time: 100 });
  attacker.anim({ x: attacker.x - 5, y: attacker.y + 10, time: 100 }, 'ballistic');
  attacker.anim({ x: attacker.x, y: attacker.y, time: 50 });
  attacker.anim({ z: attacker.behind(15), time: 200 }, 'decel');
  attacker.anim({ z: attacker.behind(-15), time: 100 }, 'accel');
  attacker.anim({ z: attacker.z, time: 300 }, 'swing');
  defender.delay(1100);
  defender.anim({ z: defender.behind(15), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `poltergeist` → `neverendingnightmare` anim (a sweeping wall of dark wisps). */
const neverendingnightmare: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  // PS: `linear-gradient(#440044 30%, #000000)` → flat dark-purple wash.
  scene.backgroundEffect('#220022', 1500, 0.6);
  const ystep = (defender.x - 200 - defender.x) / 5;
  const zstep = defender.z / 5;

  const sweep = (baseX: number, scale: number, opacity: number, timeBase: number): void => {
    const xstep = (defender.x + baseX - defender.x) / 5;
    for (let i = 0; i < 5; i++) {
      scene.showEffect(
        'poisonwisp',
        {
          x: defender.x - baseX + xstep * (i + 1),
          y: defender.y + 200 + ystep * (i + 1),
          z: defender.z + zstep * (i + 1),
          scale,
          opacity,
          time: 40 * i + timeBase,
        },
        { opacity: 0, time: 50 * i + 1000 },
        'linear',
      );
    }
  };
  sweep(200, 0.6, 0.7, 0);
  sweep(150, 0.6, 0.7, 100);
  sweep(100, 1.2, 1, 200);
  sweep(50, 0.6, 0.7, 300);
  // Mirror sweeps (PS reuses negative base offsets via `defender.x + N`).
  const mirror = (baseX: number, scale: number, opacity: number, timeBase: number): void => {
    const xstep = (defender.x - baseX - defender.x) / 5;
    for (let i = 0; i < 5; i++) {
      scene.showEffect(
        'poisonwisp',
        {
          x: defender.x + baseX + xstep * (i + 1),
          y: defender.y + 200 + ystep * (i + 1),
          z: defender.z + zstep * (i + 1),
          scale,
          opacity,
          time: 40 * i + timeBase,
        },
        { opacity: 0, time: 50 * i + 1000 },
        'linear',
      );
    }
  };
  mirror(50, 1.2, 1, 400);
  mirror(100, 0.6, 0.7, 500);
  mirror(150, 0.6, 0.7, 600);
  mirror(200, 0.6, 0.7, 700);

  scene.showEffect(
    'shadowball',
    {
      x: defender.x,
      y: defender.y - 60,
      z: defender.z,
      scale: 1,
      xscale: 3,
      opacity: 0.5,
      time: 200,
    },
    { scale: 3, xscale: 8, opacity: 0.1, time: 1300 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'shadowball',
    { x: defender.x, y: defender.y - 30, z: defender.z, opacity: 1, scale: 2, time: 200 },
    { scale: 2.5, opacity: 0, time: 1500 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'poisonwisp',
    { x: defender.x, y: defender.y, z: defender.z, opacity: 0.7, scale: 3, time: 200 },
    { scale: 9, opacity: 1, time: 1500 },
    'linear',
    'fade',
  );
};

/** PS `pounce` → `bodyslam` anim (a heavy ballistic flop that squashes the defender). */
const bodyslam: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y - 30, z: defender.z, scale: 1, time: 500 },
    { x: defender.x + 70, scale: 0.8, opacity: 0.3, time: 800 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y - 30, z: defender.z, scale: 1, time: 500 },
    { x: defender.x - 70, scale: 0.8, opacity: 0.3, time: 800 },
    'linear',
    'fade',
  );
  attacker.anim(
    { x: defender.x, y: defender.y + 80, z: defender.behind(-30), time: 400 },
    'ballistic',
  );
  attacker.anim({ x: defender.x, y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim({ time: 600 }, 'ballistic2Back');
  defender.delay(450);
  defender.anim({ y: defender.y - 30, z: defender.behind(20), yscale: 0.5, time: 200 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `powdersnow` → `icywind` anim (icy wisps + icicles spray onto the defender). */
const icywind: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1.7, opacity: 0.3 },
    {
      x: defender.x + 10,
      y: defender.y + 5,
      z: defender.behind(30),
      scale: 2.5,
      opacity: 0.4,
      time: 400,
    },
    'linear',
    'explode',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1.7, opacity: 0.3, time: 100 },
    {
      x: defender.x - 10,
      y: defender.y - 5,
      z: defender.behind(30),
      scale: 2.5,
      opacity: 0.4,
      time: 500,
    },
    'linear',
    'explode',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1.7, opacity: 0.3, time: 200 },
    {
      x: defender.x,
      y: defender.y + 5,
      z: defender.behind(30),
      scale: 2.5,
      opacity: 0.4,
      time: 600,
    },
    'linear',
    'explode',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1.7, opacity: 0.3, time: 300 },
    {
      x: defender.x,
      y: defender.y + 5,
      z: defender.behind(30),
      scale: 2.5,
      opacity: 0.4,
      time: 700,
    },
    'linear',
    'explode',
  );
  scene.showEffect(
    'icicle',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.3, opacity: 0.2 },
    { x: defender.x, y: defender.y, z: defender.behind(20), opacity: 0.6, time: 400 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'icicle',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.3, opacity: 0.2, time: 200 },
    { x: defender.x - 10, y: defender.y + 5, z: defender.behind(20), opacity: 0.6, time: 600 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'icicle',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.3, opacity: 0.2, time: 300 },
    { x: defender.x, y: defender.y - 5, z: defender.behind(20), opacity: 0.6, time: 700 },
    'linear',
    'fade',
  );
};

/** PS `powergem` — three iceball+shine orbs converge, then iceball bursts; defender rocks. */
const powergem: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y + 45, z: attacker.z, scale: 0.4, opacity: 0.2 },
    { x: defender.x - 10, y: defender.y + 5, z: defender.behind(5), opacity: 0.3, time: 300 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'iceball',
    { x: attacker.x - 45, y: attacker.y - 20, z: attacker.z, scale: 0.4, opacity: 0.2, time: 100 },
    { x: defender.x - 10, y: defender.y + 5, z: defender.behind(5), opacity: 0.3, time: 400 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'iceball',
    { x: attacker.x + 45, y: attacker.y - 20, z: attacker.z, scale: 0.4, opacity: 0.2, time: 200 },
    { x: defender.x - 10, y: defender.y + 5, z: defender.behind(5), opacity: 0.3, time: 500 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'shine',
    { x: attacker.x, y: attacker.y + 45, z: attacker.z, scale: 0.4, opacity: 0.2 },
    { x: defender.x - 10, y: defender.y + 5, z: defender.behind(5), opacity: 0.3, time: 300 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'shine',
    { x: attacker.x - 45, y: attacker.y - 20, z: attacker.z, scale: 0.4, opacity: 0.2, time: 100 },
    { x: defender.x - 10, y: defender.y + 5, z: defender.behind(5), opacity: 0.3, time: 400 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'shine',
    { x: attacker.x + 45, y: attacker.y - 20, z: attacker.z, scale: 0.4, opacity: 0.2, time: 200 },
    { x: defender.x - 10, y: defender.y + 5, z: defender.behind(5), opacity: 0.3, time: 500 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'iceball',
    { x: defender.x - 15, y: defender.y, z: defender.z, scale: 0.5, opacity: 0.5, time: 300 },
    { scale: 2, opacity: 0, time: 600 },
    'linear',
  );
  scene.showEffect(
    'iceball',
    { x: defender.x + 15, y: defender.y, z: defender.z, scale: 0.5, opacity: 0.5, time: 400 },
    { scale: 2, opacity: 0, time: 700 },
    'linear',
  );
  scene.showEffect(
    'iceball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.5, opacity: 0.5, time: 500 },
    { scale: 2, opacity: 0, time: 800 },
    'linear',
  );
  defender.delay(325);
  defender.anim({ z: defender.behind(5), time: 75 }, 'swing');
  defender.anim({ time: 75 }, 'swing');
  defender.anim({ z: defender.behind(5), time: 75 }, 'swing');
  defender.anim({ time: 75 }, 'swing');
  defender.anim({ z: defender.behind(5), time: 75 }, 'swing');
  defender.anim({ time: 150 }, 'swing');
};

/** PS `powersplit`/`powerswap` → `skillswap` anim (wisps trade between the two). */
const skillswap: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y - 30, z: attacker.z, scale: 1, opacity: 1, time: 0 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.5, opacity: 0, time: 400 },
    'ballistic2Under',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y - 30, z: attacker.z, scale: 1, opacity: 1, time: 200 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.5, opacity: 0, time: 600 },
    'ballistic2Under',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 200 },
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0, time: 600 },
    'ballistic',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 400 },
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0, time: 800 },
    'ballistic',
  );
};

/** PS `powertrick` → `selfstatus` anim. */
const powertrick: MoveAnim = selfstatus;

/** PS `powertrip` — purple wisps rise, the user swells, then a ballistic slam. */
const powertrip: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x + 30, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.4, time: 100 },
    { y: attacker.y + 60, opacity: 0.2, time: 500 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x - 30, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.4, time: 200 },
    { y: defender.y + 60, opacity: 0.2, time: 600 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x + 15, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.4, time: 300 },
    { y: attacker.y + 60, opacity: 0.2, time: 700 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x - 15, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.4, time: 400 },
    { y: attacker.y + 60, opacity: 0.2, time: 800 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y + 20, z: attacker.z, scale: 0.5, opacity: 0.1, time: 500 },
    {
      x: attacker.leftof(-30),
      y: attacker.y + 20,
      z: attacker.behind(-50),
      scale: 1,
      opacity: 0.3,
      time: 800,
    },
    'ballistic2Under',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 1250 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(30),
      scale: 1.5,
      opacity: 0,
      time: 1450,
    },
    'linear',
  );
  attacker.anim({ y: attacker.y + 5, xscale: 0.9, yscale: 1.1, time: 100 }, 'swing');
  attacker.anim({ time: 100 }, 'swing');
  attacker.anim({ y: attacker.y + 5, xscale: 0.9, yscale: 1.1, time: 100 }, 'swing');
  attacker.anim({ time: 100 }, 'swing');
  attacker.anim({ y: attacker.y + 5, xscale: 0.9, yscale: 1.1, time: 100 }, 'swing');
  attacker.anim({ time: 100 }, 'swing');
  attacker.delay(200);
  attacker.anim(
    { x: defender.x, y: defender.y + 80, z: defender.behind(-30), time: 400 },
    'ballistic',
  );
  attacker.anim({ x: defender.x, y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim({ time: 500 }, 'ballistic2Back');
  defender.delay(1250);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `poweruppunch` → `punchattack` template. */
const poweruppunch: MoveAnim = punchattack;

/** PS `powerwhip` — energy balls + scattering leaves, then a contact lunge. */
const powerwhip: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [, defender] = actors;
  scene.showEffect(
    'energyball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 420 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(20),
      scale: 3,
      opacity: 0,
      time: 700,
    },
    'linear',
  );
  scene.showEffect(
    'energyball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 520 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(20),
      scale: 3,
      opacity: 0,
      time: 800,
    },
    'linear',
  );
  scene.showEffect(
    'leaf1',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 500 },
    { x: defender.x, y: defender.y - 60, scale: 1.5, opacity: 0, time: 1100 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'leaf2',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 500 },
    { x: defender.x + 60, y: defender.y, scale: 1.5, opacity: 0, time: 1100 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'leaf2',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 500 },
    { x: defender.x, y: defender.y + 60, scale: 1.5, opacity: 0, time: 1100 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'leaf1',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 500 },
    { x: defender.x - 60, y: defender.y, scale: 1.5, opacity: 0, time: 1100 },
    'linear',
    'fade',
  );
  contactattack(scene, actors);
};

/** PS `precipiceblades` — a fiery quake erupts in a line toward the defender. */
const precipiceblades: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  // PS: `linear-gradient(#390000 30%, #B84038)` → flat deep-red wash.
  scene.backgroundEffect('#741f1c', 800, 0.4);
  scene.showEffect(
    'flareball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 1 },
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 6, opacity: 0 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'fireball',
    { x: attacker.x, y: attacker.y - 35, z: attacker.behind(-50), scale: 0, opacity: 0.8 },
    { scale: 3, opacity: 0, time: 200 },
    'linear',
  );
  scene.showEffect(
    'rock2',
    {
      x: attacker.x,
      y: attacker.y - 20,
      z: attacker.behind(-50),
      scale: 0.5,
      yscale: 1.4,
      opacity: 0.7,
      time: 25,
    },
    { y: attacker.y, yscale: 1.5, time: 250 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'fireball',
    {
      x: attacker.x,
      y: attacker.y - 35,
      z: attacker.behind(-125),
      scale: 0,
      opacity: 0.8,
      time: 150,
    },
    { scale: 3, opacity: 0 },
    'linear',
  );
  scene.showEffect(
    'rock1',
    {
      x: attacker.x,
      y: attacker.y - 20,
      z: attacker.behind(-125),
      scale: 0.5,
      yscale: 1.5,
      opacity: 0.7,
      time: 175,
    },
    { y: attacker.y, yscale: 1.6, time: 400 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'fireball',
    { x: defender.x, y: defender.y - 35, z: defender.z, scale: 0, opacity: 0.8, time: 300 },
    { scale: 6, opacity: 0 },
    'linear',
  );
  scene.showEffect(
    'rock2',
    {
      x: defender.x - 15,
      y: defender.y - 20,
      z: defender.z,
      scale: 0.5,
      yscale: 1.6,
      opacity: 0.7,
      time: 350,
    },
    { y: attacker.y, yscale: 1.7, time: 550 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'rock2',
    {
      x: defender.x + 15,
      y: defender.y - 20,
      z: defender.z,
      scale: 0.5,
      yscale: 1.6,
      opacity: 0.7,
      time: 375,
    },
    { y: attacker.y, yscale: 1.7, time: 575 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'rock1',
    {
      x: defender.x,
      y: defender.y - 20,
      z: defender.z,
      scale: 0.5,
      yscale: 1.8,
      opacity: 0.7,
      time: 400,
    },
    { y: defender.y + 10, yscale: 1.9, time: 600 },
    'accel',
    'fade',
  );
  defender.delay(325);
  defender.anim({ x: defender.x - 30, time: 75 });
  defender.anim({ x: defender.x + 30, time: 100 });
  defender.anim({ x: defender.x - 30, time: 100 });
  defender.anim({ x: defender.x + 30, time: 100 });
  defender.anim({ x: defender.x - 30, time: 100 });
  defender.anim({ x: defender.x + 30, time: 100 });
  defender.anim({ x: defender.x, time: 100 });
};

/** PS `present` — a single wrapped (iceball) gift lobs onto the defender and pops. */
const present: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.3 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 0.6, time: 500 },
    'linear',
    'explode',
  );
};

/** PS `protect` → `selfstatus` anim. */
const protect: MoveAnim = selfstatus;

/** PS `psybeam` — alternating mistball/poison wisps stream onto the defender. */
const psybeam: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'mistball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.2 },
    { x: defender.x, y: defender.y, z: defender.behind(20), opacity: 0.6, time: 200 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.2, time: 50 },
    { x: defender.x + 10, y: defender.y - 5, z: defender.behind(20), opacity: 0.6, time: 250 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'mistball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.2, time: 100 },
    { x: defender.x - 10, y: defender.y + 5, z: defender.behind(20), opacity: 0.6, time: 300 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.2, time: 150 },
    { x: defender.x, y: defender.y - 5, z: defender.behind(20), opacity: 0.6, time: 350 },
    'linear',
    'explode',
  );
};

/** PS `psychup` → `painsplit` anim (wisps pulse off both combatants). */
const painsplit: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 1, time: 0 },
    { scale: 3, opacity: 0, time: 300 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 1, time: 200 },
    { scale: 3, opacity: 0, time: 500 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 0 },
    { scale: 3, opacity: 0, time: 300 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 200 },
    { scale: 3, opacity: 0, time: 500 },
    'linear',
  );
};

/** PS `psychicfangs` — a purple wash + the jaw-snap bite, then a contact slam. */
const psychicfangs: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [attacker, defender] = actors;
  scene.backgroundEffect('#AA44BB', 450, 0.6, 450);
  biteTemplate(scene, actors);
  attacker.anim(
    { x: defender.x, y: defender.y + 80, z: defender.behind(-30), time: 400 },
    'ballistic',
  );
  attacker.anim({ x: defender.x, y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim({ time: 500 }, 'ballistic2Back');
  defender.delay(450);
  defender.anim({ z: defender.behind(20), scale: 1.2, time: 100 }, 'swing');
  defender.anim({ scale: 1, time: 300 });
};

/** PS `psychicnoise` — a mistball ripple expands, then two wisp pops on the defender. */
const psychicnoise: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'mistball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.8, time: 0 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 5, opacity: 0.1, time: 750 },
    'linear',
  );
  scene.showEffect(
    'poisonwisp',
    { x: defender.x - 20, y: defender.y + 20, z: defender.z, scale: 0, opacity: 1, time: 900 },
    { scale: 2, opacity: 0 },
    'decel',
  );
  scene.showEffect(
    'poisonwisp',
    { x: defender.x + 20, y: defender.y + 20, z: defender.z, scale: 0, opacity: 1, time: 1050 },
    { scale: 2, opacity: 0 },
    'decel',
  );
};

/** PS `psychicterrain` → `mistyterrain` anim (a pink wash + a small sway). */
const psychicterrain: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.backgroundEffect('#FF99FF', 1000, 0.3);
  dance(scene, [attacker]);
};

/** PS `psychoboost` — a black wash, mistball nova that hurls into the defender. */
const psychoboost: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.backgroundEffect('#000000', 700, 0.3);
  scene.showEffect(
    'mistball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.6, opacity: 0.5 },
    { scale: 3, opacity: 0.3, time: 300 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'mistball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.8, opacity: 0.6, time: 100 },
    { scale: 3, opacity: 0.5, time: 400 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'mistball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 2, opacity: 0.8, time: 400 },
    { x: defender.x, y: defender.y, z: defender.behind(20), scale: 1.25, time: 600 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.8, opacity: 0.6, time: 100 },
    { scale: 4, opacity: 0.5, time: 400 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 2.5, opacity: 0.8, time: 400 },
    { x: defender.x, y: defender.y, z: defender.behind(20), scale: 1.5, opacity: 0.8, time: 600 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'mistball',
    { x: defender.x + 30, y: defender.y + 25, z: defender.z, scale: 0.2, opacity: 1, time: 800 },
    { x: defender.x + 50, y: defender.y + 10, opacity: 0.5, time: 1000 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'mistball',
    { x: defender.x - 30, y: defender.y - 20, z: defender.z, scale: 0.2, opacity: 1, time: 900 },
    { x: defender.x - 50, y: defender.y - 20, opacity: 0.5, time: 1100 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'mistball',
    { x: defender.x + 15, y: defender.y + 10, z: defender.z, scale: 0.2, opacity: 1, time: 900 },
    { x: defender.x + 35, y: defender.y + 30, opacity: 0.5, time: 1100 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'mistball',
    { x: defender.x - 15, y: defender.y - 30, z: defender.z, scale: 0.2, opacity: 1, time: 800 },
    { x: defender.x - 35, y: defender.y - 40, opacity: 0.5, time: 1000 },
    'accel',
    'explode',
  );
  attacker.delay(400);
  attacker.anim({ z: attacker.behind(20), time: 300 }, 'swing');
  attacker.anim({ time: 300 }, 'swing');
  defender.delay(600);
  defender.anim({ z: defender.behind(10), time: 300 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `psychocut` — diagonal wisp slashes streak across the defender. */
const psychocut: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  scene.showEffect(
    'poisonwisp',
    { x: defender.x + 60, y: defender.y + 30, z: defender.z, scale: 0.2, opacity: 1 },
    { x: defender.x - 70, y: defender.y - 40, z: defender.z, scale: 0.4, opacity: 0.4, time: 200 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'poisonwisp',
    { x: defender.x + 60, y: defender.y + 30, z: defender.z, scale: 0.2, opacity: 1, time: 100 },
    { x: defender.x - 70, y: defender.y - 40, z: defender.z, scale: 0.4, opacity: 0.4, time: 300 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'poisonwisp',
    { x: defender.x + 80, y: defender.y + 10, z: defender.z, scale: 0.2, opacity: 1, time: 100 },
    { x: defender.x - 50, y: defender.y - 60, z: defender.z, scale: 0.4, opacity: 0.4, time: 300 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x + 80, y: defender.y + 10, z: defender.z, scale: 0.2, opacity: 1, time: 200 },
    { x: defender.x - 50, y: defender.y - 60, z: defender.z, scale: 0.4, opacity: 0.4, time: 400 },
    'linear',
    'fade',
  );
};

/** PS `psychoshift`/`reflecttype` → `painsplit` anim. */
const psychoshift: MoveAnim = painsplit;

/** PS `psyshock` — three psychic wisps burst outward around the defender. */
const psyshock: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  scene.showEffect(
    'poisonwisp',
    { x: defender.x + 40, y: defender.y, z: defender.z, scale: 0, opacity: 0.6 },
    { scale: 3, opacity: 0 },
    'decel',
  );
  scene.showEffect(
    'poisonwisp',
    { x: defender.x - 40, y: defender.y - 20, z: defender.z, scale: 0, opacity: 0.6, time: 150 },
    { scale: 3, opacity: 0 },
    'decel',
  );
  scene.showEffect(
    'waterwisp',
    { x: defender.x + 10, y: defender.y + 20, z: defender.z, scale: 0, opacity: 0.6, time: 300 },
    { scale: 3, opacity: 0 },
    'decel',
  );
};

/** PS `psystrike` — psychic terrain wash; wisps gather then burst from the defender. */
const psystrike: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  // PS used a remote weather-psychicterrain.png background; substitute a pink wash.
  scene.backgroundEffect('#FF99FF', 950, 0.6);
  scene.showEffect(
    'poisonwisp',
    { x: defender.x - 100, y: defender.y, z: defender.z, scale: 0.5, opacity: 0.2 },
    { x: defender.x, y: defender.y, scale: 1, opacity: 1, time: 250 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'poisonwisp',
    { x: defender.x + 70, y: defender.y - 70, z: defender.z, scale: 0.5, opacity: 0.2, time: 100 },
    { x: defender.x, y: defender.y, scale: 1, opacity: 1, time: 350 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'poisonwisp',
    { x: defender.x, y: defender.y + 100, z: defender.z, scale: 0.5, opacity: 0.2, time: 200 },
    { x: defender.x, y: defender.y, scale: 1, opacity: 1, time: 450 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'poisonwisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 450 },
    { x: defender.x, y: defender.y - 100, scale: 0.9, opacity: 0, time: 950 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'poisonwisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 450 },
    { x: defender.x - 60, y: defender.y + 80, scale: 0.9, opacity: 0, time: 950 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'poisonwisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 450 },
    { x: defender.x + 60, y: defender.y + 80, scale: 0.9, opacity: 0, time: 950 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'poisonwisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 450 },
    { x: defender.x - 90, y: defender.y - 40, scale: 0.9, opacity: 0, time: 950 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'poisonwisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 450 },
    { x: defender.x + 90, y: defender.y - 40, scale: 0.9, opacity: 0, time: 950 },
    'linear',
    'fade',
  );
  defender.delay(450);
  defender.anim({ z: defender.behind(10), time: 200 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `psywave` → `psybeam` anim. */
const psywave: MoveAnim = psybeam;

/** PS `punishment` → `contactattack` template. */
const punishment: MoveAnim = contactattack;

/** PS `pursuit` — the attacker dashes through the fleeing defender; a shadow trail lingers. */
const pursuit: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'shadowball',
    { x: defender.x, y: defender.y, z: defender.behind(15), scale: 0, opacity: 0.2, time: 600 },
    { scale: 1.5, opacity: 0, time: 1000 },
    'linear',
  );
  attacker.delay(300);
  attacker.anim(
    { x: defender.leftof(20), y: defender.y, z: defender.behind(-20), time: 300 },
    'ballistic2Under',
  );
  attacker.anim({ x: defender.leftof(5), y: defender.y, z: defender.behind(15), time: 50 });
  attacker.anim({ time: 500 }, 'ballistic2');
  defender.anim({ x: defender.x, y: defender.y, z: defender.behind(15), time: 600 }, 'accel');
  defender.delay(25);
  defender.anim({ x: defender.x, y: defender.y, z: defender.behind(30), time: 100 }, 'swing');
  defender.anim({ time: 400 }, 'swing');
};

/** PS `quash` — a ballistic lunge that presses the defender down with a chop. */
const quash: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  attacker.anim(
    { x: defender.x, y: defender.y + 80, z: defender.behind(-30), time: 400 },
    'ballistic',
  );
  attacker.anim({ x: defender.x, y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim({ time: 500 }, 'ballistic2Back');
  defender.delay(450);
  defender.anim({ y: defender.y - 15, yscale: 0.5, time: 300 }, 'swing');
  defender.anim({ time: 300 }, 'decel');
  scene.showEffect(
    'rightchop',
    { x: defender.x, y: defender.y + 10, z: defender.z, scale: 0.5, opacity: 0.1, time: 400 },
    { y: defender.y, scale: 0.25, opacity: 1, time: 700 },
    'decel',
    'explode',
  );
};

/** PS `quickguard` → `selfstatus` anim. */
const quickguard: MoveAnim = selfstatus;

/** PS `quiverdance` — a brown wash, a shake, and rising wisps. */
const quiverdance: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.backgroundEffect('#987058', 800, 0.3, 400);
  shake(scene, [attacker]);
  scene.showEffect(
    'wisp',
    { x: attacker.x + 40, y: attacker.y - 40, z: attacker.z, scale: 0.2, opacity: 1, time: 0 },
    { y: attacker.y + 60, opacity: 0, time: 400 },
    'accel',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x - 40, y: attacker.y - 40, z: attacker.z, scale: 0.2, opacity: 1, time: 200 },
    { y: attacker.y + 60, opacity: 0, time: 600 },
    'accel',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y - 40, z: attacker.z, scale: 0.2, opacity: 1, time: 400 },
    { y: attacker.y + 60, opacity: 0, time: 800 },
    'accel',
  );
};

/** PS `rage` → `thrash` anim (an enraged double-pummel of the defender). */
const thrash: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'angry',
    { x: attacker.x - 10, y: attacker.y + 50, z: attacker.z, scale: 0.5, opacity: 1, time: 0 },
    { scale: 3, opacity: 0, time: 300 },
    'ballistic2Under',
    'fade',
  );
  attacker.delay(300);
  attacker.anim(
    { x: defender.leftof(-30), y: defender.y + 80, z: defender.behind(-30), time: 300 },
    'ballistic',
  );
  attacker.anim({ x: defender.leftof(30), y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim(
    { x: defender.leftof(30), y: defender.y + 80, z: defender.behind(-30), time: 200 },
    'ballisticUp',
  );
  attacker.anim({ x: defender.leftof(-30), y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim({ time: 500 }, 'ballistic2Back');
  defender.delay(750);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 200 }, 'swing');
  defender.anim({ z: defender.behind(15), time: 300 }, 'decel');
  defender.anim({ time: 300 }, 'swing');
  scene.showEffect(
    'foot',
    { x: defender.x - 10, y: defender.y + 20, z: defender.z, scale: 1, opacity: 1, time: 650 },
    {
      x: defender.x - 15,
      y: defender.y + 10,
      z: defender.behind(15),
      scale: 2,
      opacity: 0,
      time: 950,
    },
    'linear',
  );
  scene.showEffect(
    'fist',
    { x: defender.x - 5, y: defender.y - 5, z: defender.z, scale: 0.6, opacity: 0.6, time: 675 },
    { x: defender.x - 10, y: defender.y - 10, z: defender.z, scale: 1, opacity: 1, time: 875 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'fist',
    { x: defender.x + 10, y: defender.y + 10, z: defender.z, scale: 0.6, opacity: 0.6, time: 700 },
    { x: defender.x + 20, y: defender.y + 20, z: defender.z, scale: 1, opacity: 1, time: 900 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'fist',
    { x: defender.x + 20, y: defender.y - 30, z: defender.z, scale: 0.6, opacity: 0.6, time: 725 },
    { x: defender.x + 30, y: defender.y - 25, z: defender.z, scale: 1, opacity: 1, time: 925 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'foot',
    { x: defender.x, y: defender.y + 20, z: defender.z, scale: 1, opacity: 1, time: 1000 },
    { x: defender.x, y: defender.y + 10, z: defender.behind(15), scale: 2, opacity: 0, time: 1300 },
    'linear',
  );
};

/** PS `ragefist` — a black wash, angry marks + shadow vortex, then a dark fist strike. */
const ragefist: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.backgroundEffect('#000000', 900, 0.3);
  scene.showEffect(
    'angry',
    { x: attacker.x + 20, y: attacker.y + 10, z: attacker.z, scale: 0.5, opacity: 0.7, time: 0 },
    { scale: 1.2, opacity: 1, time: 350 },
    'ballistic2Under',
    'fade',
  );
  scene.showEffect(
    'angry',
    { x: attacker.x - 20, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.7, time: 100 },
    { scale: 1.2, opacity: 1, time: 550 },
    'ballistic2Under',
    'fade',
  );
  scene.showEffect(
    'angry',
    { x: attacker.x, y: attacker.y + 30, z: attacker.z, scale: 0.5, opacity: 0.7, time: 250 },
    { scale: 1.2, opacity: 1, time: 700 },
    'ballistic2Under',
    'fade',
  );
  scene.showEffect(
    'shadowball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.8, time: 150 },
    { scale: 2, opacity: 0, time: 1300 },
    'decel',
  );
  scene.showEffect(
    'shadowball',
    { x: attacker.x + 100, y: attacker.y + 60, z: attacker.z, scale: 0.5, opacity: 0.7, time: 100 },
    { x: attacker.x, y: attacker.y, scale: 0.3, opacity: 0, time: 600 },
    'decel',
  );
  scene.showEffect(
    'shadowball',
    { x: attacker.x - 90, y: attacker.y + 30, z: attacker.z, scale: 0.7, opacity: 0.6, time: 200 },
    { x: attacker.x, y: attacker.y, scale: 0.4, opacity: 0, time: 675 },
    'decel',
  );
  scene.showEffect(
    'shadowball',
    { x: attacker.x + 70, y: attacker.y - 40, z: attacker.z, scale: 0.6, opacity: 0.6, time: 300 },
    { x: attacker.x, y: attacker.y, scale: 0.3, opacity: 0, time: 750 },
    'decel',
  );
  scene.showEffect(
    'shadowball',
    { x: attacker.x - 80, y: attacker.y - 30, z: attacker.z, scale: 0.6, opacity: 0.6, time: 400 },
    { x: attacker.x, y: attacker.y, scale: 0.3, opacity: 0, time: 800 },
    'decel',
  );
  scene.showEffect(
    'shadowball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 2, opacity: 0.2, time: 300 },
    { scale: 1, opacity: 0.5, time: 800 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'shadowball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.8, time: 750 },
    { scale: 2, opacity: 0, time: 1050 },
    'decel',
  );
  scene.backgroundEffect('#000000', 400, 0);
  attacker.delay(1100);
  attacker.anim(
    { x: defender.x, y: defender.y, z: defender.behind(-20), opacity: 0, time: 275 },
    'linear',
  );
  attacker.anim({ time: 275 }, 'linear');
  attacker.anim({ opacity: 1, time: 525 }, 'linear');
  scene.showEffect(
    'shadowball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.8, time: 1375 },
    { scale: 2, opacity: 0, time: 1950 },
    'decel',
  );
  scene.showEffect(
    'fist',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 1375 },
    { scale: 2, opacity: 0, time: 1850 },
    'decel',
  );
  defender.delay(1370);
  defender.anim(
    { x: defender.leftof(15), y: defender.y, z: defender.behind(20), time: 50 },
    'swing',
  );
  defender.anim({ time: 500 }, 'swing');
};

/** PS `ragepowder` → `selfstatus` anim. */
const ragepowder: MoveAnim = selfstatus;

/** PS `raging-bull` → `gigaimpact` anim (a dark wash + an impact tackle). */
const gigaimpact: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.backgroundEffect('#000000', 700, 0.2);
  scene.showEffect(
    'impact',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.4, time: 300 },
    { scale: 4, opacity: 0, time: 600 },
    'linear',
  );
  scene.showEffect(
    'impact',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.4, time: 500 },
    { scale: 4, opacity: 0, time: 800 },
    'linear',
  );
  // PS drew two attacker-sprite streaks here (showEffect(attacker.sp, …)); omitted
  // (no sprite-as-effect in our engine) — the attacker's tackle below carries the read.
  attacker.anim({ x: defender.x, y: defender.y, z: defender.behind(-5), time: 300 }, 'accel');
  attacker.anim({ time: 500 }, 'ballistic2Back');
  defender.delay(280);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `ragingfury` — a red wash, angry/black wisps, fireballs, then a fierce lunge. */
const ragingfury: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  // PS: `linear-gradient(#390000 30%, #B84038)` → flat deep-red wash.
  scene.backgroundEffect('#741f1c', 600, 0.6, 400);
  scene.showEffect(
    'angry',
    { x: attacker.x - 10, y: attacker.y + 50, z: attacker.z, scale: 0.5, opacity: 1, time: 0 },
    { scale: 3, opacity: 0, time: 300 },
    'ballistic2Under',
    'fade',
  );
  scene.showEffect(
    'blackwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 1, time: 0 },
    { x: attacker.x - 50, y: attacker.y - 50, scale: 2, opacity: 0, time: 300 },
    'ballistic',
  );
  scene.showEffect(
    'blackwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 1, time: 150 },
    { x: attacker.x + 60, y: attacker.y - 50, scale: 2, opacity: 0, time: 450 },
    'ballistic',
  );
  scene.showEffect(
    'blackwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 1, time: 300 },
    { x: attacker.x + 10, y: attacker.y - 60, scale: 2, opacity: 0, time: 600 },
    'ballistic',
  );
  scene.showEffect(
    'flareball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.5, time: 600 },
    { scale: 4, opacity: 0, time: 900 },
    'linear',
  );
  scene.showEffect(
    'flareball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.5, time: 800 },
    { scale: 4, opacity: 0, time: 1100 },
    'linear',
  );
  attacker.delay(300);
  attacker.anim(
    { x: defender.leftof(20), y: defender.y, z: defender.behind(-5), time: 300 },
    'accel',
  );
  attacker.anim({ time: 500 }, 'ballistic2Back');
  defender.delay(580);
  defender.anim({ z: defender.behind(20), time: 200 }, 'decel');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `raindance` → `dance` anim. */
const raindance: MoveAnim = dance;

/** PS `rapidspin` → `spinattack` anim. */
const rapidspin: MoveAnim = spinattack;

/** PS `razorshell` — an iceball + slash + a flung shell, then a contact lunge. */
const razorshell: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [, defender] = actors;
  scene.showEffect(
    'iceball',
    { x: defender.x + 10, y: defender.y - 10, z: defender.z, scale: 1.5, opacity: 0.6, time: 700 },
    { scale: 2, opacity: 0, time: 1000 },
    'accel',
    'fade',
  );
  scene.showEffect(
    'leftslash',
    { x: defender.x - 10, y: defender.y - 10, z: defender.z, scale: 1.5, opacity: 0.6, time: 600 },
    { scale: 2, opacity: 0, time: 1000 },
    'accel',
    'fade',
  );
  scene.showEffect(
    'shell',
    { x: defender.x - 60, y: defender.y + 70, z: defender.z, scale: 0.75, opacity: 1, time: 400 },
    { x: defender.x + 60, y: defender.y - 70, scale: 0.6, opacity: 0, time: 700 },
    'decel',
  );
  contactattack(scene, actors);
};

/** PS `razorwind` — a charge move: `selfstatus` prepares; the release is a contact hit. */
const razorwind: MoveAnimEntry = {
  // PS defined `anim: null!`; the engine resolves a null release to the contact hit.
  anim: contactattack,
  prepareAnim: selfstatus,
};

/** PS `recover` → `chargestatus` anim. */
const recover: MoveAnim = chargestatus;

/** PS `recycle` → `selfstatus` anim. */
const recycle: MoveAnim = selfstatus;

/** PS `reflect` — no animation (empty in PS). */
const reflect: MoveAnim = () => {};

/** PS `refresh` — an energy + rainbow pulse around the user. */
const refresh: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'energyball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.5 },
    { scale: 1.5, opacity: 0, time: 500 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'rainbow',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 1 },
    { scale: 2, opacity: 0, time: 500 },
    'linear',
    'fade',
  );
};

/** PS `rest` — two soft wisps rise behind the user as it falls asleep. */
const rest: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y + 20, z: attacker.z, scale: 0.5, opacity: 0.1 },
    {
      x: attacker.x,
      y: attacker.y + 20,
      z: attacker.behind(-50),
      scale: 1.5,
      opacity: 1,
      time: 400,
    },
    'ballistic2Under',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y + 20, z: attacker.z, scale: 0.5, opacity: 0.1, time: 200 },
    {
      x: attacker.x,
      y: attacker.y + 20,
      z: attacker.behind(-50),
      scale: 1.5,
      opacity: 1,
      time: 600,
    },
    'ballistic2Under',
    'fade',
  );
};

/** PS `return` — a heart, then a flurry of fists + foot stomps in a loving combo. */
const returnMove: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'heart',
    { x: attacker.x - 10, y: attacker.y + 50, z: attacker.z, scale: 0.5, opacity: 1, time: 0 },
    { scale: 3, opacity: 0, time: 300 },
    'ballistic2Under',
    'fade',
  );
  attacker.delay(300);
  attacker.anim(
    { x: defender.leftof(-30), y: defender.y + 80, z: defender.behind(-30), time: 300 },
    'ballistic',
  );
  attacker.anim({ x: defender.leftof(30), y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim(
    { x: defender.leftof(30), y: defender.y + 80, z: defender.behind(-30), time: 200 },
    'ballisticUp',
  );
  attacker.anim({ x: defender.leftof(-30), y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim({ time: 500 }, 'ballistic2Back');
  defender.delay(750);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 200 }, 'swing');
  defender.anim({ z: defender.behind(15), time: 300 }, 'decel');
  defender.anim({ time: 300 }, 'swing');
  scene.showEffect(
    'foot',
    { x: defender.x - 10, y: defender.y + 20, z: defender.z, scale: 1, opacity: 1, time: 650 },
    {
      x: defender.x - 15,
      y: defender.y + 10,
      z: defender.behind(15),
      scale: 2,
      opacity: 0,
      time: 950,
    },
    'linear',
  );
  scene.showEffect(
    'fist',
    { x: defender.x - 5, y: defender.y - 5, z: defender.z, scale: 0.6, opacity: 0.6, time: 675 },
    { x: defender.x - 10, y: defender.y - 10, z: defender.z, scale: 1, opacity: 1, time: 875 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'fist',
    { x: defender.x + 10, y: defender.y + 10, z: defender.z, scale: 0.6, opacity: 0.6, time: 700 },
    { x: defender.x + 20, y: defender.y + 20, z: defender.z, scale: 1, opacity: 1, time: 900 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'fist',
    { x: defender.x + 20, y: defender.y - 30, z: defender.z, scale: 0.6, opacity: 0.6, time: 725 },
    { x: defender.x + 30, y: defender.y - 25, z: defender.z, scale: 1, opacity: 1, time: 925 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'foot',
    { x: defender.x, y: defender.y + 20, z: defender.z, scale: 1, opacity: 1, time: 1000 },
    { x: defender.x, y: defender.y + 10, z: defender.behind(15), scale: 2, opacity: 0, time: 1300 },
    'linear',
  );
};

/** PS `retaliate` → `closecombat` anim (a barrage of fists + impact flashes). */
const closecombat: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  attacker.anim(
    { x: defender.leftof(-30), y: defender.y + 80, z: defender.behind(-30), time: 350 },
    'ballistic',
  );
  attacker.anim({ x: defender.leftof(30), y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim(
    { x: defender.leftof(30), y: defender.y + 80, z: defender.behind(-30), time: 150 },
    'ballisticUp',
  );
  attacker.anim({ x: defender.leftof(-30), y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim({ time: 500 }, 'ballistic2Back');
  defender.delay(450);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 200 }, 'swing');
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
  scene.showEffect(
    'fist',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.6, opacity: 0.6, time: 425 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 525 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'fist',
    { x: defender.x - 10, y: defender.y + 20, z: defender.z, scale: 0.6, opacity: 0.6, time: 450 },
    { x: defender.x - 20, y: defender.y + 30, z: defender.z, scale: 1, opacity: 1, time: 550 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'fist',
    { x: defender.x + 30, y: defender.y - 20, z: defender.z, scale: 0.6, opacity: 0.6, time: 475 },
    { x: defender.x + 35, y: defender.y - 30, z: defender.z, scale: 1, opacity: 1, time: 575 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'fist',
    { x: defender.x - 30, y: defender.y - 20, z: defender.z, scale: 0.6, opacity: 0.6, time: 575 },
    { x: defender.x - 35, y: defender.y - 30, z: defender.z, scale: 1, opacity: 1, time: 775 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'fist',
    { x: defender.x, y: defender.y - 10, z: defender.z, scale: 0.6, opacity: 0.6, time: 600 },
    { x: defender.x + 10, y: defender.y - 15, z: defender.z, scale: 1, opacity: 1, time: 750 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'fist',
    { x: defender.x, y: defender.y + 10, z: defender.z, scale: 0.6, opacity: 0.6, time: 650 },
    { x: defender.x - 10, y: defender.y + 15, z: defender.z, scale: 1, opacity: 1, time: 800 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'impact',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.4, time: 525 },
    { scale: 3, opacity: 0, time: 825 },
    'linear',
  );
  scene.showEffect(
    'impact',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.4, time: 750 },
    { scale: 3, opacity: 0, time: 1050 },
    'linear',
  );
};

/** PS `reversal` → `contactattack` template. */
const reversal: MoveAnim = contactattack;

/** PS `roar` → `sound` anim. */
const roar: MoveAnim = sound;

/** PS `rockblast` — a single rock3 hurled at the defender. */
const rockblast: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'rock3',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.6, opacity: 0.4 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.6, opacity: 1, time: 400 },
    'linear',
    'explode',
  );
};

/** PS `rockclimb` → `slam` → `contactattack` template. */
const rockclimb: MoveAnim = contactattack;

/** PS `rockpolish` — diagonal slashes + sparkles + rising shine on the user. */
const rockpolish: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'leftslash',
    { x: attacker.x - 10, y: attacker.y - 10, z: attacker.z, scale: 0.7, opacity: 0.3 },
    { opacity: 0, time: 300 },
    'decel',
  );
  scene.showEffect(
    'rightslash',
    { x: attacker.x + 20, y: attacker.y - 15, z: attacker.z, scale: 0.7, opacity: 0.3, time: 100 },
    { opacity: 0, time: 400 },
    'decel',
  );
  scene.showEffect(
    'leftslash',
    { x: attacker.x - 5, y: attacker.y + 15, z: attacker.z, scale: 0.7, opacity: 0.3, time: 200 },
    { opacity: 0, time: 500 },
    'decel',
  );
  scene.showEffect(
    'rightslash',
    { x: attacker.x + 5, y: attacker.y - 10, z: attacker.z, scale: 0.7, opacity: 0.3, time: 200 },
    { opacity: 0, time: 500 },
    'decel',
  );
  scene.showEffect(
    'shine',
    { x: attacker.x - 10, y: attacker.y - 10, z: attacker.z, scale: 0.3, opacity: 0.6 },
    { opacity: 0, time: 300 },
    'decel',
  );
  scene.showEffect(
    'shine',
    { x: attacker.x + 20, y: attacker.y - 15, z: attacker.z, scale: 0.3, opacity: 0.6, time: 100 },
    { opacity: 0, time: 400 },
    'decel',
  );
  scene.showEffect(
    'shine',
    { x: attacker.x - 5, y: attacker.y + 15, z: attacker.z, scale: 0.3, opacity: 0.6, time: 200 },
    { opacity: 0, time: 500 },
    'decel',
  );
  scene.showEffect(
    'shine',
    { x: attacker.x + 5, y: attacker.y - 10, z: attacker.z, scale: 0.3, opacity: 0.6, time: 300 },
    { opacity: 0, time: 600 },
    'accel',
  );
  scene.showEffect(
    'shine',
    { x: attacker.x + 20, y: attacker.y - 30, z: attacker.z, scale: 0.2, opacity: 1, time: 0 },
    { y: attacker.y + 40, opacity: 0, time: 400 },
    'accel',
  );
  scene.showEffect(
    'shine',
    { x: attacker.x - 20, y: attacker.y + 40, z: attacker.z, scale: 0.2, opacity: 1, time: 200 },
    { y: attacker.y + 40, opacity: 0, time: 600 },
    'accel',
  );
  scene.showEffect(
    'shine',
    { x: attacker.x, y: attacker.y + 30, z: attacker.z, scale: 0.2, opacity: 1, time: 400 },
    { y: attacker.y + 40, opacity: 0, time: 800 },
    'accel',
  );
};

/** PS `rocksmash` → `punchattack` template. */
const rocksmash: MoveAnim = punchattack;

/** PS `rockthrow` — a single rock1 thrown ballistically at the defender. */
const rockthrow: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'rock1',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.3, opacity: 0 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 400 },
    'ballistic',
    'explode',
  );
};

/* ──────────────────────────────────────────────────────────────────────────
 * The batch table, keyed by Showdown move id (toID form).
 * ──────────────────────────────────────────────────────────────────────── */

export const SHOWDOWN_BATCH_6: Record<string, MoveAnim | MoveAnimEntry> = {
  poisonpowder,
  poisonsting: poisonjab,
  poisontail: poisonjab,
  pollenpuff: revelationdance,
  poltergeist: neverendingnightmare,
  pounce: bodyslam,
  powdersnow: icywind,
  powergem,
  powersplit: skillswap,
  powerswap: skillswap,
  powertrick,
  powertrip,
  poweruppunch,
  powerwhip,
  precipiceblades,
  present,
  protect,
  psybeam,
  psychup: painsplit,
  psychicfangs,
  psychicnoise,
  psychicterrain,
  psychoboost,
  psychocut,
  psychoshift,
  psyshock,
  psystrike,
  psywave,
  punishment,
  pursuit,
  quash,
  quickguard,
  quiverdance,
  rage: thrash,
  ragefist,
  ragepowder,
  ragingbull: gigaimpact,
  ragingfury,
  raindance,
  rapidspin,
  razorleaf: magicalleaf,
  razorshell,
  razorwind,
  recover,
  recycle,
  reflect,
  reflecttype: psychoshift,
  refresh,
  rest,
  retaliate: closecombat,
  return: returnMove,
  revenge: contactattack,
  reversal,
  risingvoltage: discharge,
  roar,
  rockblast,
  rockclimb,
  rockpolish,
  rocksmash,
  rockthrow,
};

/* ──────────────────────────────────────────────────────────────────────────
 * Remaining alias targets ported above-the-fold but referenced once: keep their
 * defining functions here so the table reads cleanly.
 * ──────────────────────────────────────────────────────────────────────── */

/** PS `razorleaf` → `magicalleaf` anim (a fan of leaves flung at the defender). */
function magicalleaf(scene: FxScene, [attacker, defender]: Actor[]): void {
  scene.showEffect(
    'leaf1',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1.1, opacity: 1 },
    { x: defender.x + 30, y: defender.y + 30, z: defender.z, scale: 2, opacity: 0.6, time: 200 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'leaf2',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1.1, opacity: 1, time: 75 },
    { x: defender.x + 20, y: defender.y - 30, z: defender.z, scale: 2, opacity: 0.6, time: 275 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'leaf1',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1.1, opacity: 1, time: 150 },
    { x: defender.x - 30, y: defender.y, z: defender.z, scale: 2, opacity: 0.6, time: 350 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'leaf2',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1.1, opacity: 1, time: 225 },
    { x: defender.x - 10, y: defender.y + 10, z: defender.z, scale: 2, opacity: 0.6, time: 425 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'leaf1',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1.1, opacity: 1, time: 300 },
    { x: defender.x + 10, y: defender.y - 10, z: defender.z, scale: 2, opacity: 0.6, time: 500 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'leaf2',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1.1, opacity: 1, time: 375 },
    { x: defender.x - 20, y: defender.y, z: defender.z, scale: 2, opacity: 0.6, time: 575 },
    'linear',
    'explode',
  );
}

/** PS `risingvoltage` → `discharge` anim (three expanding electro rings). */
function discharge(scene: FxScene, [attacker]: Actor[]): void {
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.2, time: 0 },
    { scale: 8, opacity: 0.1, time: 600 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.2, time: 150 },
    { scale: 8, opacity: 0.1, time: 750 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.2, time: 300 },
    { scale: 8, opacity: 0.1, time: 900 },
    'linear',
    'fade',
  );
}
